<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

import RevenueExpenseChart  from './component/charts/RevenueExpenseChart.vue'
import ExpenseBreakdownChart from './component/charts/ExpenseBreakdownChart.vue'
import ProfitTrendChart     from './component/charts/ProfitTrendChart.vue'
import Header               from './component/header.vue'

const router  = useRouter()
const data    = ref({})
const loading = ref(false)

// Active tab: 'all' | 'overdue' | 'due' | 'upcoming'
const payTab  = ref('all')

const load = async () => {
  try {
    loading.value = true
    const res  = await axios.get('/dashboard/accounting')
    data.value = res.data
  } catch (err) {
    console.error(err)
  } finally {
    loading.value = false
  }
}

const fmt = (value) => {
  if (!value && value !== 0) return '0'
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const fmtDate = (d) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

const daysDiff = (dateStr) => {
  if (!dateStr) return null
  const diff = Math.round((new Date(dateStr) - new Date()) / 86400000)
  return diff
}

const dayLabel = (item) => {
  if (!item.due_date) {
    return item.trigger_basis === 'GOODS_RECEIPT_DATE' ? 'Awaiting GRN' : ''
  }
  const d = daysDiff(item.due_date)
  if (d === null) return ''
  if (item.status === 'overdue') return `${Math.abs(d)} day${Math.abs(d) !== 1 ? 's' : ''} overdue`
  if (d === 0) return 'Due today'
  if (d > 0) return `In ${d} day${d !== 1 ? 's' : ''}`
  return `${Math.abs(d)} day${Math.abs(d) !== 1 ? 's' : ''} overdue`
}

const vp = computed(() => data.value?.vendor_payments ?? { summary: {}, items: [] })

const filteredItems = computed(() => {
  const items = vp.value.items ?? []
  if (payTab.value === 'overdue')  return items.filter(i => i.status === 'overdue')
  if (payTab.value === 'due')      return items.filter(i => i.status === 'due')
  if (payTab.value === 'upcoming') return items.filter(i => i.status === 'pending')
  return items
})

const goToPo = (poId) => router.push({ name: 'PO Detail', params: { id: poId } })

onMounted(load)
</script>

<template>
  <div>
    <Header />

    <div class="dash">

      <!-- ── Stat Cards ── -->
      <div class="stat-grid">

        <template v-if="loading">
          <div class="stat-card" v-for="n in 3" :key="n">
            <div class="skeleton skeleton--icon"></div>
            <div>
              <div class="skeleton skeleton--label"></div>
              <div class="skeleton skeleton--value mt-2"></div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="stat-card">
            <div class="stat-icon stat-icon--blue">
              <font-awesome-icon icon="wallet" />
            </div>
            <div>
              <p class="stat-label">Cash Balance</p>
              <p class="stat-value">{{ fmt(data.balances?.cash) }}</p>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon stat-icon--green">
              <font-awesome-icon icon="file-invoice-dollar" />
            </div>
            <div>
              <p class="stat-label">Receivables</p>
              <p class="stat-value stat-value--green">{{ fmt(data.balances?.receivables) }}</p>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon stat-icon--red">
              <font-awesome-icon icon="hand-holding-usd" />
            </div>
            <div>
              <p class="stat-label">Payables</p>
              <p class="stat-value stat-value--red">{{ fmt(data.balances?.payables) }}</p>
            </div>
          </div>
        </template>

      </div>

      <!-- ── Vendor Payments ── -->
      <div class="vp-card">
        <div class="vp-header">
          <div class="chart-title-block">
            <div class="chart-icon chart-icon--orange">
              <font-awesome-icon icon="file-invoice" />
            </div>
            <div>
              <p class="chart-title">Vendor Payments Due</p>
              <p class="chart-sub">PO payment obligations — overdue, due &amp; upcoming (30 days)</p>
            </div>
          </div>
        </div>

        <!-- Summary chips -->
        <div class="vp-chips" v-if="!loading">
          <div class="vp-chip vp-chip--red" :class="{ active: payTab === 'overdue' }" @click="payTab = payTab === 'overdue' ? 'all' : 'overdue'">
            <span class="chip-count">{{ vp.summary.count_overdue ?? 0 }}</span>
            <span class="chip-label">Overdue</span>
            <span class="chip-amount">{{ fmt(vp.summary.total_overdue) }}</span>
          </div>
          <div class="vp-chip vp-chip--yellow" :class="{ active: payTab === 'due' }" @click="payTab = payTab === 'due' ? 'all' : 'due'">
            <span class="chip-count">{{ vp.summary.count_due ?? 0 }}</span>
            <span class="chip-label">Due Now</span>
            <span class="chip-amount">{{ fmt(vp.summary.total_due) }}</span>
          </div>
          <div class="vp-chip vp-chip--blue" :class="{ active: payTab === 'upcoming' }" @click="payTab = payTab === 'upcoming' ? 'all' : 'upcoming'">
            <span class="chip-count">{{ vp.summary.count_upcoming ?? 0 }}</span>
            <span class="chip-label">Upcoming</span>
            <span class="chip-amount">{{ fmt(vp.summary.total_upcoming) }}</span>
          </div>
        </div>
        <div v-if="loading" class="vp-chips">
          <div class="skeleton" style="height:64px;border-radius:10px;flex:1" v-for="n in 3" :key="n"></div>
        </div>

        <!-- Table -->
        <div class="vp-table-wrap" v-if="!loading">
          <table class="vp-table" v-if="filteredItems.length">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>PO #</th>
                <th>Description</th>
                <th class="num">Amount Due</th>
                <th>Due Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in filteredItems"
                :key="item.id"
                class="vp-row"
                :class="`vp-row--${!item.due_date && item.status === 'pending' ? 'awaiting' : item.status === 'pending' ? 'upcoming' : item.status}`"
                @click="goToPo(item.po_id)"
              >
                <td class="vendor-cell">{{ item.vendor_name }}</td>
                <td class="po-cell">{{ item.po_number }}</td>
                <td class="desc-cell">{{ item.description }}</td>
                <td class="num amount-cell">{{ item.currency }} {{ fmt(item.amount_remaining) }}</td>
                <td>
                  <span class="due-date">{{ item.due_date ? fmtDate(item.due_date) : 'TBD' }}</span>
                  <span
                    class="day-label"
                    :class="!item.due_date ? 'day-label--awaiting' : `day-label--${item.status === 'pending' ? 'upcoming' : item.status}`"
                  >{{ dayLabel(item) }}</span>
                </td>
                <td>
                  <span :class="['status-badge', !item.due_date ? 'status-badge--awaiting' : `status-badge--${item.status === 'pending' ? 'upcoming' : item.status}`]">
                    {{ item.status === 'overdue' ? 'Overdue' : item.status === 'due' ? 'Due' : !item.due_date ? 'Pending GRN' : 'Upcoming' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="vp-empty">
            <font-awesome-icon icon="check-circle" class="vp-empty-icon" />
            <p>No pending vendor payments{{ payTab !== 'all' ? ` in this category` : '' }}</p>
          </div>
        </div>
        <div v-if="loading" class="skeleton skeleton--chart" style="height:160px"></div>
      </div>

      <!-- ── Charts ── -->
      <div class="chart-grid">

        <div class="chart-card chart-card--full">
          <div class="chart-header">
            <div class="chart-title-block">
              <div class="chart-icon chart-icon--blue">
                <font-awesome-icon icon="chart-bar" />
              </div>
              <div>
                <p class="chart-title">Revenue vs Expense</p>
                <p class="chart-sub">Monthly comparison</p>
              </div>
            </div>
          </div>
          <div v-if="loading" class="skeleton skeleton--chart"></div>
          <RevenueExpenseChart v-else :data="data.revenue_vs_expense" />
        </div>

        <div class="chart-card">
          <div class="chart-header">
            <div class="chart-title-block">
              <div class="chart-icon chart-icon--orange">
                <font-awesome-icon icon="chart-pie" />
              </div>
              <div>
                <p class="chart-title">Expense Breakdown</p>
                <p class="chart-sub">By category</p>
              </div>
            </div>
          </div>
          <div v-if="loading" class="skeleton skeleton--chart"></div>
          <ExpenseBreakdownChart v-else :data="data.expense_breakdown" />
        </div>

        <div class="chart-card">
          <div class="chart-header">
            <div class="chart-title-block">
              <div class="chart-icon chart-icon--green">
                <font-awesome-icon icon="arrow-up" />
              </div>
              <div>
                <p class="chart-title">Profit Trend</p>
                <p class="chart-sub">Net profit over time</p>
              </div>
            </div>
          </div>
          <div v-if="loading" class="skeleton skeleton--chart"></div>
          <ProfitTrendChart v-else :data="data.profit_trend" />
        </div>

      </div>

    </div>
  </div>
</template>

<style scoped>
.dash { padding:24px; display:flex; flex-direction:column; gap:20px; }

/* ── Stat Cards ── */
.stat-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; }

.stat-card {
  display:flex; align-items:center; gap:16px;
  background:#fff; border:1px solid #e4eaf2; border-radius:12px;
  padding:20px 24px;
}

.stat-icon {
  width:52px; height:52px; border-radius:14px; flex-shrink:0;
  display:flex; align-items:center; justify-content:center;
  font-size:22px;
}
.stat-icon--blue  { background:rgba(0,74,173,.1);   color:#004AAD; }
.stat-icon--green { background:rgba(22,163,74,.1);  color:#16a34a; }
.stat-icon--red   { background:rgba(224,53,53,.1);  color:#e03535; }

.stat-label { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin:0 0 4px; }
.stat-value { font-size:26px; font-weight:800; color:#1a2b45; margin:0; line-height:1; }
.stat-value--green { color:#16a34a; }
.stat-value--red   { color:#e03535; }

/* ── Vendor Payments Card ── */
.vp-card {
  background:#fff; border:1px solid #e4eaf2; border-radius:12px;
  padding:24px; display:flex; flex-direction:column; gap:20px;
}

.vp-header { display:flex; align-items:center; justify-content:space-between; }

.vp-chips { display:flex; gap:12px; }

.vp-chip {
  flex:1; border-radius:10px; padding:14px 16px;
  display:flex; flex-direction:column; gap:2px;
  cursor:pointer; transition:box-shadow .15s, transform .12s;
  border:2px solid transparent;
}
.vp-chip:hover { transform:translateY(-1px); box-shadow:0 4px 12px rgba(0,0,0,.08); }
.vp-chip.active { border-color: currentColor; }

.vp-chip--red    { background:rgba(224,53,53,.07);   color:#c0392b; }
.vp-chip--yellow { background:rgba(234,179,8,.09);   color:#92610a; }
.vp-chip--blue   { background:rgba(0,74,173,.07);    color:#004AAD; }

.chip-count  { font-size:22px; font-weight:800; line-height:1; }
.chip-label  { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; opacity:.8; }
.chip-amount { font-size:13px; font-weight:700; margin-top:4px; }

/* ── Table ── */
.vp-table-wrap { overflow-x:auto; }

.vp-table {
  width:100%; border-collapse:collapse;
  font-size:13px; color:#374151;
}
.vp-table th {
  text-align:left; font-size:11px; font-weight:600; text-transform:uppercase;
  letter-spacing:.04em; color:#7a90b0; border-bottom:1px solid #e4eaf2;
  padding:8px 12px; white-space:nowrap;
}
.vp-table th.num, .vp-table td.num { text-align:right; }

.vp-row {
  cursor:pointer; transition:background .12s;
}
.vp-row:hover { background:#f8fafc; }
.vp-row td { padding:11px 12px; border-bottom:1px solid #f0f4f8; vertical-align:middle; }

.vp-row--overdue  td:first-child { border-left:3px solid #e03535; }
.vp-row--due      td:first-child { border-left:3px solid #e08c00; }
.vp-row--upcoming td:first-child { border-left:3px solid #004AAD; }
.vp-row--awaiting td:first-child { border-left:3px solid #9ca3af; }

.vendor-cell { font-weight:600; color:#1a2b45; }
.po-cell     { font-weight:600; color:#004AAD; font-size:12px; }
.desc-cell   { color:#6b7280; font-size:12px; }
.amount-cell { font-weight:700; color:#1a2b45; }

.due-date  { display:block; font-weight:500; color:#374151; }
.day-label { display:block; font-size:11px; margin-top:2px; }
.day-label--overdue  { color:#e03535; font-weight:600; }
.day-label--due      { color:#b45309; font-weight:600; }
.day-label--upcoming { color:#004AAD; }
.day-label--awaiting { color:#6b7280; font-style:italic; }

.status-badge {
  display:inline-block; padding:2px 10px; border-radius:20px;
  font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; white-space:nowrap;
}
.status-badge--overdue  { background:rgba(224,53,53,.12);  color:#c0392b; }
.status-badge--due      { background:rgba(234,179,8,.15);  color:#92610a; }
.status-badge--upcoming { background:rgba(0,74,173,.1);    color:#004AAD; }
.status-badge--awaiting { background:rgba(107,114,128,.1); color:#4b5563; }

.vp-empty {
  text-align:center; padding:40px 20px; color:#9ca3af;
  display:flex; flex-direction:column; align-items:center; gap:10px;
}
.vp-empty-icon { font-size:32px; color:#86efac; }
.vp-empty p    { margin:0; font-size:14px; }

/* ── Chart Grid ── */
.chart-grid  { display:grid; grid-template-columns:repeat(2,1fr); gap:16px; }
.chart-card  {
  background:#fff; border:1px solid #e4eaf2; border-radius:12px;
  padding:24px; display:flex; flex-direction:column; gap:20px;
}
.chart-card--full { grid-column:1 / -1; }

.chart-header     { display:flex; align-items:center; justify-content:space-between; }
.chart-title-block { display:flex; align-items:center; gap:12px; }
.chart-icon       { width:38px; height:38px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.chart-icon--blue   { background:rgba(0,74,173,.1);   color:#004AAD; }
.chart-icon--orange { background:rgba(224,140,0,.1);  color:#e08c00; }
.chart-icon--green  { background:rgba(22,163,74,.1);  color:#16a34a; }
.chart-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.chart-sub   { font-size:12px; color:#7a90b0; margin:0; }

/* ── Skeleton ── */
.skeleton {
  border-radius:8px;
  background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%);
  background-size:200% 100%;
  animation:shimmer 1.4s infinite;
}
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.skeleton--icon  { width:52px; height:52px; border-radius:14px; flex-shrink:0; }
.skeleton--label { height:14px; width:80px; }
.skeleton--value { height:28px; width:130px; }
.skeleton--chart { height:260px; }
.mt-2 { margin-top:8px; }

/* ── Responsive ── */
@media(max-width:991px) {
  .stat-grid  { grid-template-columns:repeat(2,1fr); }
  .chart-grid { grid-template-columns:1fr; }
  .chart-card--full { grid-column:auto; }
  .vp-chips   { flex-wrap:wrap; }
  .vp-chip    { min-width:calc(50% - 6px); }
}
@media(max-width:576px) {
  .dash       { padding:16px; }
  .stat-grid  { grid-template-columns:1fr; }
  .stat-value { font-size:22px; }
  .vp-chip    { min-width:100%; }
}
</style>
